import { Button, Card, Spinner } from "@closer/ui";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";

/**
 * Yeni e-posta adresinin onay ekranı (CLOS-225 `[048]`).
 *
 * ### ⚠ Oturum GEREKTİRMEZ
 *
 * Kullanıcı bağlantıya yeni adresinin posta kutusundan, çoğu zaman BAŞKA bir tarayıcıdan
 * ya da telefondan tıklıyor. `RequireAuth` arkasında olsaydı `/login`'e atılır ve token
 * yolda kaybolurdu — `/verify-email` ve `/davet` ile aynı gerekçe.
 *
 * ### Neden otomatik gönderiyor
 *
 * Kullanıcı zaten "onaylıyorum" niyetiyle bağlantıya tıkladı; ikinci bir düğme, aynı
 * kararı iki kez sormak olurdu. Talebin kendisi zaten paroladan geçmişti.
 */
type Durum = "isleniyor" | "tamam" | "hata";

export function EmailChangeConfirmPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const { refreshSession } = useAuth();
  const navigate = useNavigate();
  const [durum, setDurum] = useState<Durum>(token ? "isleniyor" : "hata");
  const [mesaj, setMesaj] = useState<string | null>(
    token ? null : "Doğrulama bağlantısı eksik veya bozuk.",
  );
  /** ⚠ Tek kullanımlık token: React'in çift efekt koşusu ikinci isteği "geçersiz" yapardı. */
  const gonderildi = useRef(false);

  useEffect(() => {
    if (!token || gonderildi.current) return;
    gonderildi.current = true;

    void (async () => {
      try {
        await api<{ email: string }>("/api/profile/email-change/confirm", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        /**
         * Oturum yükü e-postayı taşıyor; tazelenmezse kullanıcı aynı tarayıcıda
         * eski adresini görmeye devam ederdi. Oturumu olmayan bir ziyaretçide bu
         * çağrı zararsızca boş döner.
         */
        await refreshSession();
        setDurum("tamam");
      } catch (error) {
        setDurum("hata");
        // Metin SUNUCUDAN; ekran kendi gerekçesini uydurmuyor (refleks 4).
        setMesaj(
          error instanceof ApiRequestError
            ? error.message
            : "İşlem tamamlanamadı. Lütfen tekrar deneyin.",
        );
      }
    })();
  }, [token, refreshSession]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <Card title="E-posta Adresi Doğrulama" className="w-full max-w-sm">
        {durum === "isleniyor" ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : durum === "tamam" ? (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-strong">
              Yeni e-posta adresiniz doğrulandı. Bundan sonra bu adresle giriş yapacaksınız.
            </p>
            <Button onClick={() => navigate("/app")}>Uygulamaya dön</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p role="alert" className="text-sm text-danger">
              {mesaj}
            </p>
            {/* Çıkışsız bırakmıyoruz (CLOS-496 sınıfı): kullanıcı buradan bir yere gidebilmeli. */}
            <Link
              to="/login"
              className="text-center text-sm font-medium text-primary hover:underline"
            >
              Giriş sayfasına dön
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
